import React, { useState, useEffect } from "react";
import { IUniform } from "../../interfaces/IUniform";
import { getUniformsByPosition } from "../../services/uniform.service.ts";

interface PositionUniformsInfoProps {
  positionId: number;
  onTotalAmountChange?: (amount: number) => void;
  hideIfEmpty?: boolean;
}

const PositionUniformsInfo: React.FC<PositionUniformsInfoProps> = ({
  positionId,
  onTotalAmountChange,
  hideIfEmpty = false,
}) => {
  const [uniforms, setUniforms] = useState<IUniform[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (positionId) {
      fetchUniforms();
    } else {
      setUniforms([]);
      setTotalAmount(0);
    }
  }, [positionId]);

  const fetchUniforms = async () => {
    setLoading(true);
    try {
      const data = await getUniformsByPosition(positionId);
      setUniforms(data);

      const total = data.reduce((sum, uniform) => {
        const price =
          typeof uniform.unitPrice === "string"
            ? parseFloat(uniform.unitPrice)
            : uniform.unitPrice;
        return sum + price;
      }, 0);
      setTotalAmount(total);
      onTotalAmountChange?.(total);
    } catch (error) {
      console.error("Error fetching uniforms:", error);
      setUniforms([]);
      setTotalAmount(0);
      onTotalAmountChange?.(0);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | string): string => {
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return numPrice.toFixed(2);
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <span className="ms-2">Cargando uniformes...</span>
      </div>
    );
  }

  if (!positionId) {
    return (
      <div className="alert alert-warning mb-3">
        <i className="fas fa-exclamation-triangle me-2"></i>
        <strong>No se ha seleccionado un puesto</strong>
        <br />
        <small>PositionId: {positionId}</small>
      </div>
    );
  }

  if (uniforms.length === 0) {
    if (hideIfEmpty) return null;
    return (
      <div className="alert alert-info mb-3">
        <i className="fas fa-info-circle me-2"></i>
        <strong>Este puesto no tiene uniformes asignados</strong>
        <br />
        <small>PositionId: {positionId}</small>
      </div>
    );
  }

  const isVacancyData =
    uniforms.length > 0 && uniforms[0].hasOwnProperty("isAvailable");

  if (isVacancyData) {
    return (
      <div className="mb-3">
        <div className="card border-0 bg-light">
          <div className="card-body p-3">
            <h6 className="card-title mb-3 text-dark">
              <i className="fas fa-briefcase me-2"></i>
              Vacantes Disponibles para el Puesto
            </h6>

            <div className="row">
              {uniforms.map((vacancy, index) => (
                <div key={vacancy.id || index} className="col-md-6 mb-3">
                  <div
                    className="card border h-100"
                    style={{
                      borderRadius: "6px",
                      transition: "box-shadow 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div className="card-header bg-light border-bottom p-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0 fw-semibold text-dark">
                          {vacancy.branch?.name || "Sin sucursal"}
                        </h6>
                        <span
                          className={`badge ${
                            vacancy.isAvailable ? "bg-success" : "bg-secondary"
                          } text-white`}
                        >
                          {vacancy.isAvailable ? "Disponible" : "No disponible"}
                        </span>
                      </div>
                    </div>

                    <div className="card-body p-3">
                      <div className="mb-2">
                        <small className="text-muted">Puesto:</small>
                        <div className="fw-semibold text-dark">
                          {vacancy.position?.name || "Sin puesto"}
                        </div>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted">Empresa:</small>
                        <div className="text-dark">
                          {vacancy.businessName?.name || "Sin empresa"}
                        </div>
                      </div>

                      {vacancy.isPriority && (
                        <div className="mb-2">
                          <span className="badge bg-warning text-dark">
                            <i className="fas fa-star me-1"></i>
                            Prioridad
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 pt-3 border-top">
              <div className="d-flex justify-content-between align-items-center">
                <strong className="text-dark">Total de Vacantes:</strong>
                <span className="badge bg-primary fs-6">{uniforms.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <div className="">
        <div className="">
          <h6 className="card-title mb-3 text-dark">
            <i className="fas fa-tshirt me-2"></i>
            Uniformes del Puesto
          </h6>

          <div className="row">
            {uniforms.map((uniform, index) => (
              <div key={uniform.id || index} className="col-md-4 col-lg-3 mb-2">
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded border">
                  <div className="flex-grow-1 me-2">
                    <div className="fw-semibold text-dark small">
                      {uniform.name}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {uniform.description}
                    </div>
                  </div>
                  <span className="badge bg-secondary small">
                    ${formatPrice(uniform.unitPrice)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionUniformsInfo;
